/**
 * App Component
 *
 * The root component of the application that sets up the navigation container
 * and includes the bottom navigation component (BottomNavComponent) to render
 * the tab navigation throughout the app.
 *
 * Main Components:
 * 1. BottomNavComponent: Handles the bottom navigation with several tabs.
 * 2. NavigationContainer: Wraps the BottomNavComponent and manages navigation state.
 * 3. View Container: A wrapper to apply background color and handle layout.
 *
 * Dependencies:
 *  React Navigation for handling navigation.
 *  BottomNavComponent for the tabbased navigation system.
 *  COLORS from constants for theming and styling.
 */
import React from "react";
import BottomNavComponent from "./components/BottomNavComponent/BottomNavComponent";
import { NavigationContainer } from "@react-navigation/native";
import { PaperProvider } from "react-native-paper";
import { View, StyleSheet, Platform } from "react-native";
import { COLORS } from "./constants";
import InstallPromptWeb from "./components/InstallPromptWeb/InstallPromptWeb";
import { logAnalyticsEvent } from "./utils/firebase";

// Disable console logs in production mode
if (process.env.NODE_ENV === "production") {
  console.info("Production mode: Disabling console logs");
  console.log = () => {};
  console.error = () => {};
  console.warn = () => {};
}

const LoadScript = Platform.OS == "web" ? require("@react-google-maps/api").LoadScript : React.Fragment;

const linking = {
  prefixes: ["http://localhost:3000/#/"],
  config: {
    screens: {
      Parking: {
        screens: {
          ParkingListView: "parking",
          ParkingMapView: "parking/map",
          ParkingDetailsView: "parking/details",
        },
      },
      Attractions: {
        screens: {
          AttractionsListView: "attractions",
          AttractionsMapView: "attractions/map",
          AttractionsDetailsView: "attractions/details",
        },
      },
      Bathrooms: {
        screens: {
          BathroomListView: "bathrooms",
          BathroomMapView: "bathrooms/map",
          BathroomDetailsView: "bathrooms/details",
        },
      },
      Settings: "settings",
      Events: {
        screens: {
          ListView: "events",
          FiltersView: "events/filters",
          DetailsView: "events/details",
        },
      },
    },
  },
  ...(Platform.OS === "web" && {
    getInitialURL: async () => window.location.href,
  }),
};
/**
 * App Component
 *
 * This is the entry point for the application. It renders the NavigationContainer
 * and wraps the BottomNavComponent inside a View container. The NavigationContainer
 * manages the app's navigation flow, while the BottomNavComponent handles the bottom tabs.
 */
const App = () => {
  logAnalyticsEvent("screen_view", { name: "VBGo-App" });

  return (
    <LoadScript {...(Platform.OS == "web" ? { googleMapsApiKey: "AIzaSyCbk0Qick_JINm5uspq60xYumyUb3xYQO4" } : {})}>
      <View style={styles.container}>
        <PaperProvider>
          <NavigationContainer linking={linking}>
            <BottomNavComponent />
          </NavigationContainer>
        </PaperProvider>
        {Platform.OS === "web" && <InstallPromptWeb />}
      </View>
    </LoadScript>
  );
};

export default App;

/**
 * Styles for the App component
 *
 * container: Defines the overall layout of the app, ensures the app takes up the
 *            entire screen, and sets the background color from the constants file.
 */
const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: COLORS.vbgoBlue,
  },
});
